import React, { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import styled from '@emotion/styled';

import { CaptionText1, FlexColumn, Toggle } from '@portal/common/components';
import { FORECAST_SCENARIO_ID_KEY } from '@portal/common/models/data-key';
import { DataFilters } from '@portal/common/types';

import _ from '../../locale';
import {
    getChartSelectedRefinementFilters,
    getNormalizedQueriedRefinementFilters,
} from '../../store/data-explorer/selectors';

const Container = styled(FlexColumn)(({ theme }) => ({
    padding: 20,
    marginTop: 80,
    height: 'fit-content',
    alignItems: 'flex-start',
    gap: 10,
    borderRadius: theme.borderRadius.base,
    background: theme.color.white,
    boxShadow: '0px 2px 10px 0px rgba(0, 0, 0, 0.06)',
    border: `1px solid ${theme.color.gray15}`,
}));

const StyledToggle = styled(Toggle)({
    marginBottom: 10,
    ':last-of-type': {
        marginBottom: 40,
    },
});

const StyledCaption = styled(CaptionText1)({
    marginBottom: 15,
});

type Props = {
    onChange: (filters: DataFilters) => void;
};

const ForecastScenarioOption: FC<{
    id: number;
    selectedIds: number[];
    onChange: (id: number, isSelected: boolean) => void;
}> = ({ id, selectedIds, onChange }) => {
    const isSelected = useMemo(() => selectedIds?.includes(id), [selectedIds, id]);
    const name = _(`forecast_scenario_${id}`);
    const updateForecastScenarios = useCallback(
        ({ target: { checked } }) => {
            onChange(id, checked);
        },
        [onChange, id]
    );

    return (
        <StyledToggle
            checked={isSelected}
            name={name}
            label={name}
            onChange={updateForecastScenarios}
        />
    );
};

const ForecastScenariosSelector: FC<Props> = ({ onChange }) => {
    const chartFilters = useSelector(getChartSelectedRefinementFilters);
    const refinementFilters = useSelector(getNormalizedQueriedRefinementFilters);

    const options = refinementFilters?.[FORECAST_SCENARIO_ID_KEY] ?? [];
    const selectedScenarios = chartFilters?.[FORECAST_SCENARIO_ID_KEY] ?? options;

    const updateForecastScenarios = useCallback(
        (id: number, isSelected: boolean) => {
            const updatedFilters = {
                [FORECAST_SCENARIO_ID_KEY]: isSelected
                    ? [...selectedScenarios, id]
                    : selectedScenarios.filter((selectedId) => selectedId !== id),
            };
            onChange(updatedFilters as DataFilters);
        },
        [selectedScenarios, onChange]
    );

    return (
        <Container>
            <StyledCaption>{_('dengue_forecast_scenarios_selector_title')}</StyledCaption>
            {options.map((id: number) => (
                <ForecastScenarioOption
                    key={id}
                    id={id}
                    selectedIds={selectedScenarios}
                    onChange={updateForecastScenarios}
                />
            ))}
        </Container>
    );
};

export default ForecastScenariosSelector;
